import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectCurrentUser, selectAccessToken, logoutUser } from '../store/authSlice';
import { selectFavorites, selectShinies, removeFavorite, removeShiny } from '../store/userDataSlice';
import { authFetch } from '../utils/api';
import { parsePokepaste, teamToPoképaste, resolvePokedexIds } from '../utils/pokepaste';
import './UserProfile.css';

const EMPTY_FORM = { name: '', folder: 'Uncategorized', description: '', pokepaste: '' };

function UserProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectAccessToken);
  const favorites = useSelector(selectFavorites);
  const shinies = useSelector(selectShinies);

  const [teams, setTeams] = useState([]);
  const [quizHistory, setQuizHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('teams');

  // Modal state
  const [modal, setModal] = useState(null); // null | 'create'
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  // Copy-to-clipboard feedback per team
  const [copiedId, setCopiedId] = useState(null);

  const folders = [...new Set(teams.map(t => t.folder || 'Uncategorized'))];

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [teamsRes, quizRes] = await Promise.all([
        authFetch('/user/teams'),
        authFetch('/user/quiz-history'),
      ]);
      const [teamsData, quizData] = await Promise.all([
        teamsRes.json(),
        quizRes.json(),
      ]);
      setTeams(teamsData.teams || []);
      setQuizHistory(quizData.quizHistory || []);
    } catch (err) {
      console.error('Failed to load profile data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/');
  };

  const handleRemoveFavorite = (pokedexId) => {
    dispatch(removeFavorite(pokedexId));
  };

  const handleRemoveShiny = (pokedexId) => {
    dispatch(removeShiny(pokedexId));
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      await authFetch(`/user/teams/${teamId}`, { method: 'DELETE' });
      setTeams(prev => prev.filter(t => t._id !== teamId));
    } catch (err) {
      console.error('Failed to delete team');
    }
  };

  const handleExport = async (team) => {
    const text = teamToPoképaste(team.pokemon);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(team._id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // fallback: select a textarea
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedId(team._id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const openCreateModal = () => {
    setForm({ ...EMPTY_FORM, folder: folders[0] || 'Uncategorized' });
    setModalError('');
    setModal('create');
  };

  const closeModal = () => {
    setModal(null);
    setForm(EMPTY_FORM);
    setModalError('');
  };

  const handleFormChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveTeam = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setModalError('Team name is required.');
      return;
    }
    setSubmitting(true);
    setModalError('');

    try {
      let pokemon = [];

      if (form.pokepaste.trim()) {
        const parsed = parsePokepaste(form.pokepaste);
        if (parsed.length === 0) {
          setModalError('Could not parse the pokepaste. Make sure the format is correct.');
          setSubmitting(false);
          return;
        }
        if (parsed.length > 6) {
          setModalError('A team can have at most 6 Pokémon.');
          setSubmitting(false);
          return;
        }
        pokemon = await resolvePokedexIds(parsed);
      }

      const res = await authFetch('/user/teams', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          folder: form.folder.trim() || 'Uncategorized',
          description: form.description.trim(),
          pokemon,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save team');

      setTeams(prev => [...prev, data.team]);
      closeModal();
    } catch (err) {
      setModalError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const quizTypeLabel = { type: 'Type Quiz', speed: 'Speed Quiz', damage: 'Damage Calc', battle: 'Battle Quiz' };

  // Group teams by folder, preserving order folders were first seen
  const orderedFolders = [...new Set(teams.map(t => t.folder || 'Uncategorized'))];
  const teamsByFolder = orderedFolders.reduce((acc, folder) => {
    acc[folder] = teams.filter(t => (t.folder || 'Uncategorized') === folder);
    return acc;
  }, {});

  const TABS = [
    { key: 'teams', label: 'My Teams' },
    { key: 'favorites', label: 'Favorites' },
    { key: 'shinies', label: '✦ Shiny Dex' },
    { key: 'quizzes', label: 'Quiz History' },
  ];

  return (
    <div className="profile-page">
      {/* Header card */}
      <div className="profile-header-card">
        <div className="profile-header-banner" />
        <div className="profile-header-body">
          <div className="profile-avatar">
            {user?.avatar
              ? <img src={user.avatar} alt={user.displayName} referrerPolicy="no-referrer" />
              : <span>{user?.displayName?.[0]?.toUpperCase() || '?'}</span>
            }
          </div>
          <div className="profile-info">
            <h1 className="profile-name">{user?.displayName}</h1>
            <p className="profile-email">{user?.email}</p>
            <p className="profile-since">Trainer since {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}</p>
          </div>
          <button className="profile-logout" onClick={handleLogout}>Log Out</button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="profile-stats">
        <div className="profile-stat">
          <span className="profile-stat-value">{teams.length}</span>
          <span className="profile-stat-label">Teams</span>
        </div>
        <div className="profile-stat">
          <span className="profile-stat-value">{favorites.length}</span>
          <span className="profile-stat-label">Favorites</span>
        </div>
        <div className="profile-stat">
          <span className="profile-stat-value">{shinies.length}</span>
          <span className="profile-stat-label">Shinies Seen</span>
        </div>
        <div className="profile-stat">
          <span className="profile-stat-value">{quizHistory.length}</span>
          <span className="profile-stat-label">Quizzes Taken</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`profile-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="profile-content">
        {loading ? (
          <p className="profile-empty">Loading...</p>
        ) : (
          <>
            {/* ─── Teams tab ─── */}
            {activeTab === 'teams' && (
              <div>
                <div className="teams-toolbar">
                  <button className="btn-new-team" onClick={openCreateModal}>+ New Team</button>
                </div>

                {teams.length === 0 ? (
                  <p className="profile-empty">No teams saved yet. Import a pokepaste to get started!</p>
                ) : (
                  orderedFolders.map(folder => (
                    <div key={folder} className="folder-section">
                      <div className="folder-header">
                        <span className="folder-icon">📁</span>
                        <span className="folder-name">{folder}</span>
                        <span className="folder-count">{teamsByFolder[folder].length}</span>
                      </div>

                      <div className="profile-teams">
                        {teamsByFolder[folder].map(team => (
                          <div key={team._id} className="team-card">
                            <div className="team-card-header">
                              <h3>{team.name}</h3>
                              <div className="team-card-actions">
                                <button
                                  className={`team-export${copiedId === team._id ? ' copied' : ''}`}
                                  onClick={() => handleExport(team)}
                                  aria-label="Export as pokepaste"
                                  title="Copy pokepaste to clipboard"
                                >
                                  {copiedId === team._id ? '✓ Copied' : '↑ Export'}
                                </button>
                                <button
                                  className="team-delete"
                                  onClick={() => handleDeleteTeam(team._id)}
                                  aria-label="Delete team"
                                >
                                  ×
                                </button>
                              </div>
                            </div>

                            {team.description && <p className="team-description">{team.description}</p>}

                            <div className="team-pokemon-list">
                              {team.pokemon.length === 0 ? (
                                <span className="team-empty">Empty team</span>
                              ) : (
                                team.pokemon.map((p, i) => (
                                  <span key={i} className="team-pokemon-chip">{p.name}</span>
                                ))
                              )}
                            </div>

                            <p className="team-updated">
                              Updated {new Date(team.updatedAt || team.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ─── Favorites tab ─── */}
            {activeTab === 'favorites' && (
              <div className="profile-favorites">
                {favorites.length === 0 ? (
                  <p className="profile-empty">No favorites yet. Star Pokémon from their detail pages!</p>
                ) : (
                  <div className="fav-grid">
                    {favorites.map(fav => (
                      <div key={fav.pokedexId} className="fav-card">
                        <a href={`/pokemon/${fav.pokedexId}`} className="fav-card-link">
                          <img
                            src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${fav.pokedexId}.png`}
                            alt={fav.name}
                            className="fav-sprite"
                            onError={(e) => {
                              e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${fav.pokedexId}.png`;
                            }}
                          />
                          <span className="fav-name">
                            {fav.name.charAt(0).toUpperCase() + fav.name.slice(1)}
                          </span>
                          <span className="fav-number">#{String(fav.pokedexId).padStart(3, '0')}</span>
                        </a>
                        <button
                          className="fav-remove"
                          onClick={() => handleRemoveFavorite(fav.pokedexId)}
                          aria-label={`Remove ${fav.name} from favorites`}
                          title="Remove from favorites"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ─── Shiny Dex tab ─── */}
            {activeTab === 'shinies' && (
              <div className="profile-shinies">
                {shinies.length === 0 ? (
                  <p className="profile-empty">No shinies recorded yet. Mark them from the Pokémon detail pages!</p>
                ) : (
                  <div className="shiny-grid">
                    {shinies.map(shiny => (
                      <div key={shiny.pokedexId} className="shiny-card">
                        <a href={`/pokemon/${shiny.pokedexId}`} className="shiny-card-link">
                          <img
                            src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${shiny.pokedexId}.png`}
                            alt={`Shiny ${shiny.name}`}
                            className="shiny-sprite"
                            onError={(e) => { e.target.style.opacity = '0.3'; }}
                          />
                          <span className="shiny-name">
                            {shiny.name.charAt(0).toUpperCase() + shiny.name.slice(1)}
                          </span>
                          <span className="shiny-number">#{String(shiny.pokedexId).padStart(3, '0')}</span>
                        </a>
                        <button
                          className="shiny-remove"
                          onClick={() => handleRemoveShiny(shiny.pokedexId)}
                          aria-label={`Remove ${shiny.name} from shiny dex`}
                          title="Remove from Shiny Dex"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ─── Quiz history tab ─── */}
            {activeTab === 'quizzes' && (
              <div className="profile-quizzes">
                {quizHistory.length === 0 ? (
                  <p className="profile-empty">No quiz history yet. Try the quizzes!</p>
                ) : (
                  <table className="quiz-table">
                    <thead>
                      <tr>
                        <th>Quiz</th>
                        <th>Score</th>
                        <th>%</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...quizHistory].reverse().map(entry => (
                        <tr key={entry._id}>
                          <td>{quizTypeLabel[entry.quizType] || entry.quizType}</td>
                          <td>{entry.score} / {entry.total}</td>
                          <td>
                            <span className={`quiz-pct ${entry.percentage >= 80 ? 'good' : entry.percentage >= 50 ? 'ok' : 'poor'}`}>
                              {entry.percentage}%
                            </span>
                          </td>
                          <td>{new Date(entry.completedAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Create / Import Modal ─── */}
      {modal === 'create' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Team</h2>
              <button className="modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>

            <form onSubmit={handleSaveTeam}>
              {modalError && <div className="modal-error">{modalError}</div>}

              <div className="modal-field">
                <label htmlFor="team-name">Team Name</label>
                <input
                  id="team-name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleFormChange}
                  placeholder="e.g. My Reg G Team"
                  autoFocus
                />
              </div>

              <div className="modal-field">
                <label htmlFor="team-folder">Regulation / Folder</label>
                <input
                  id="team-folder"
                  name="folder"
                  type="text"
                  list="folder-options"
                  value={form.folder}
                  onChange={handleFormChange}
                  placeholder="e.g. Reg G"
                />
                <datalist id="folder-options">
                  {folders.map(f => <option key={f} value={f} />)}
                </datalist>
              </div>

              <div className="modal-field">
                <label htmlFor="team-description">Description <span className="label-optional">(optional)</span></label>
                <input
                  id="team-description"
                  name="description"
                  type="text"
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Short notes about this team"
                />
              </div>

              <div className="modal-field">
                <label htmlFor="team-pokepaste">
                  Pokepaste <span className="label-optional">(optional — paste from Pokémon Showdown)</span>
                </label>
                <textarea
                  id="team-pokepaste"
                  name="pokepaste"
                  value={form.pokepaste}
                  onChange={handleFormChange}
                  placeholder={`Garchomp @ Choice Scarf\nAbility: Rough Skin\nLevel: 50\nEVs: 4 HP / 252 Atk / 252 Spe\nJolly Nature\n- Dragon Claw\n- Earthquake\n- Stone Edge\n- Fire Fang`}
                  rows={10}
                />
                {form.pokepaste.trim() && (() => {
                  const count = parsePokepaste(form.pokepaste).length;
                  return (
                    <span className="paste-preview">
                      {count > 0 ? `✓ ${count} Pokémon detected` : '⚠ No Pokémon found — check format'}
                    </span>
                  );
                })()}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-save" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfile;
